import React, { useState } from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import { formatImageKey } from "../utils/string"

// Default placeholder for missing images
const DEFAULT_PLACEHOLDER = 'fallback.jpg';

/**
 * A reusable ResponsiveImage component that handles responsive images with Cloudflare image delivery
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Source image path/key
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.className - CSS class for the img element
 * @param {boolean} props.maintainAspectRatio - Whether to maintain 16:9 aspect ratio
 * @param {string} props.layout - Layout style: "card" (3 per row), "hero" (1 per row), or "thumbnail" (small fixed size)
 * @param {Object} props.imgAttributes - Additional attributes for the img element
 * @param {Function} props.onError - Optional callback when image fails to load
 * @param {string} props.fallbackSrc - Optional fallback image source when main image fails to load
 * @param {boolean} props.forceUseFallback - If true, always use the fallback image without trying the original first
 * @param {boolean} props.skipLazyLoading - If true, load the image immediately without lazy loading
 * @returns {React.ReactElement} - Picture element with responsive sources
 */
const ResponsiveImage = ({ 
  src, 
  alt, 
  className = "w-full", 
  maintainAspectRatio = true,
  layout = "card",
  imgAttributes = {},
  onError = null,
  fallbackSrc = DEFAULT_PLACEHOLDER,
  forceUseFallback = false,
  skipLazyLoading = false
}) => {
  // Create a ref for direct DOM access
  const imgRef = React.useRef(null);
  // State to track image loading errors
  const [hasError, setHasError] = useState(forceUseFallback);
  const [errorDetails, setErrorDetails] = useState(null);
  
  // If forceUseFallback is true, set error state immediately
  React.useEffect(() => {
    if (forceUseFallback && !hasError) {
      setHasError(true);
    }
  }, [forceUseFallback, hasError]);
  
  // Set up direct DOM error handler
  React.useEffect(() => {
    // Only run if we have a ref and we're not already in error state
    if (imgRef.current && !hasError) {
      // Direct DOM error handler
      imgRef.current.onerror = function(e) {
        // Prevent infinite error loops
        if (hasError) return;
        
        console.log('Direct DOM error handler triggered for:', src);
        
        // Store error details
        const details = {
          src,
          formattedSrc: formatImageKey(src),
          layout,
          timestamp: new Date().toISOString(),
          component: imgAttributes['data-component'] || 'unknown',
          error: 'dom-error',
          target: e.target?.src || 'unknown'
        };
        
        setErrorDetails(details);
        setHasError(true);
        
        // Call custom error handler if provided
        if (typeof onError === 'function') {
          onError(details);
        }
        
        return true; // Prevent default error handling
      };
    }
  }, [imgRef, hasError, src, layout, imgAttributes, onError]);
  
  // Fetch the image host URL from site metadata
  const data = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            imageHost
          }
        }
      }
    `
  );
  
  // Get the image host URL from site metadata
  const { imageHost } = data.site.siteMetadata;
  
  // Define size configurations based on layout
  const sizeConfigs = {
    card: [
      // Small mobile phones (1 card per row)
      { media: "(max-width: 375px)", width: 375, height: 211 },
      // Medium mobile phones (1 card per row)
      { media: "(max-width: 480px)", width: 480, height: 270 },
      // Large mobile phones (1 card per row)
      { media: "(max-width: 640px)", width: 640, height: 360 },
      // Small tablets (3 cards per row)
      { media: "(max-width: 768px)", width: 256, height: 144 },
      // Medium tablets (3 cards per row)
      { media: "(max-width: 960px)", width: 320, height: 180 },
      // Large tablets/small laptops (3 cards per row)
      { media: "(max-width: 1024px)", width: 341, height: 192 },
      // Medium laptops (3 cards per row)
      { media: "(max-width: 1280px)", width: 426, height: 240 },
      // Large laptops and desktops (3 cards per row)
      { media: "(min-width: 1281px)", width: 480, height: 270 }
    ],
    featured: [
      // Small mobile phones (3 per row)
      { media: "(max-width: 375px)", width: 125, height: 70 },
      // Medium mobile phones (3 per row)
      { media: "(max-width: 480px)", width: 160, height: 90 },
      // Large mobile phones (3 per row)
      { media: "(max-width: 640px)", width: 213, height: 120 },
      // Small tablets (6 per row)
      { media: "(max-width: 768px)", width: 128, height: 72 },
      // Medium tablets (6 per row)
      { media: "(max-width: 960px)", width: 160, height: 90 },
      // Large tablets/small laptops (6 per row)
      { media: "(max-width: 1024px)", width: 170, height: 96 },
      // Medium laptops (6 per row)
      { media: "(max-width: 1280px)", width: 213, height: 120 },
      // Large laptops and desktops (6 per row)
      { media: "(min-width: 1281px)", width: 256, height: 144 }
    ],
    hero: [
      // Small mobile phones
      { media: "(max-width: 375px)", width: 375, height: 211, quality: 80 },
      // Medium mobile phones
      { media: "(max-width: 480px)", width: 480, height: 270, quality: 80 },
      // Large mobile phones
      { media: "(max-width: 640px)", width: 640, height: 360, quality: 80 },
      // Small tablets
      { media: "(max-width: 768px)", width: 768, height: 432, quality: 75 },
      // Medium tablets
      { media: "(max-width: 960px)", width: 960, height: 540, quality: 75 },
      // Large tablets/small laptops
      { media: "(max-width: 1024px)", width: 1024, height: 576, quality: 70 },
      // Medium laptops and larger devices
      { media: "(min-width: 1025px)", width: 1280, height: 720, quality: 70 }
    ],
    thumbnail: [
      // Fixed size thumbnail with different densities
      { media: "all", width: 40, height: maintainAspectRatio ? 23 : 40 }
    ]
  };
  
  // Get the appropriate size configuration
  const sizes = sizeConfigs[layout] || sizeConfigs.card;
  
  // Default fallback size, quality, and fit parameter
  const fallbackSize = layout === "hero" 
    ? { width: 1280, height: 720, quality: 70, fit: "cover" } 
    : layout === "thumbnail" 
      ? { width: 40, height: maintainAspectRatio ? 23 : 40, quality: 80, fit: "cover" } 
      : layout === "featured"
        ? { width: 256, height: 144, quality: 80, fit: "cover" }
        : { width: 480, height: 270, quality: 80, fit: "cover" };
  
  return (
    <picture>
      {sizes.map((size, index) => {
        // Skip the last size if it's a min-width media query for the fallback image
        if (index === sizes.length - 1 && size.media.includes("min-width")) {
          const fit = size.fit || "cover";
          return (
            <source
              key={`source-${index}`}
              media={size.media}
              srcSet={`
                ${imageHost}/${formatImageKey(src)}/width=${size.width},${maintainAspectRatio ? `height=${size.height},` : ''}fit=${fit},format=auto,quality=${size.quality || 80} 1x,
                ${imageHost}/${formatImageKey(src)}/width=${size.width},${maintainAspectRatio ? `height=${size.height},` : ''}fit=${fit},format=auto,quality=${size.quality || 80},dpr=2 2x
              `}
            />
          );
        }
        
        const fit = size.fit || "cover";
        return (
          <source
            key={`source-${index}`}
            media={size.media}
            srcSet={`
              ${imageHost}/${formatImageKey(src)}/width=${size.width},${maintainAspectRatio ? `height=${size.height},` : ''}fit=${fit},format=auto,quality=${size.quality || 80} 1x,
              ${imageHost}/${formatImageKey(src)}/width=${size.width},${maintainAspectRatio ? `height=${size.height},` : ''}fit=${fit},format=auto,quality=${size.quality || 80},dpr=2 2x
            `}
          />
        );
      })}
      
      {/* Fallback image with error handling */}
      <img
        ref={imgRef}
        alt={alt}
        className={className}
        loading={skipLazyLoading ? "eager" : "lazy"}
        fetchpriority={skipLazyLoading ? "high" : "auto"}
        decoding="async"
        crossOrigin="anonymous"
        data-main-image=""
        width={fallbackSize.width}
        height={maintainAspectRatio ? fallbackSize.height : undefined}
        src={hasError ? `${imageHost}/${fallbackSrc}/width=${fallbackSize.width},${maintainAspectRatio ? `height=${fallbackSize.height},` : ''}fit=${fallbackSize.fit || "cover"},format=auto,quality=${fallbackSize.quality || 80}` : `${imageHost}/${formatImageKey(src)}/width=${fallbackSize.width},${maintainAspectRatio ? `height=${fallbackSize.height},` : ''}fit=${fallbackSize.fit || "cover"},format=auto,quality=${fallbackSize.quality || 80}`}
        // Use both React's onError and direct HTML onerror attribute
        onError={(e) => {
          // Prevent infinite error loops
          if (hasError) return;
          
          // Log error details with more information
          const errorMsg = `Image failed to load: ${src} (likely a 404 error)`;
          console.error(errorMsg, e);
          
          // Store error details
          const details = {
            src,
            formattedSrc: formatImageKey(src),
            layout,
            timestamp: new Date().toISOString(),
            component: imgAttributes['data-component'] || 'unknown',
            error: e.type || 'error',
            target: e.target?.src || 'unknown'
          };
          setErrorDetails(details);
          
          // Set error state to trigger fallback
          setHasError(true);
          
          // Call custom error handler if provided
          if (typeof onError === 'function') {
            onError(details);
          }
          
          // If window.errorReporter exists, use it (can be implemented elsewhere)
          if (typeof window !== 'undefined' && window.errorReporter) {
            window.errorReporter.reportImageError(details);
          }
          
          // Force a re-render to ensure the fallback image is displayed
          setTimeout(() => {
            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
              window.requestAnimationFrame(() => {
                // This empty callback just forces a re-render
              });
            }
          }, 0);
        }}
        {...imgAttributes}
      />
      
      {/* Show error message in development */}
      {hasError && process.env.NODE_ENV === 'development' && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-2" role="alert">
          <strong className="font-bold">Image Error:</strong>
          <span className="block sm:inline"> Failed to load image: {errorDetails?.formattedSrc}</span>
        </div>
      )}
    </picture>
  );
};

ResponsiveImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  maintainAspectRatio: PropTypes.bool,
  layout: PropTypes.oneOf(["card", "hero", "thumbnail", "featured"]),
  imgAttributes: PropTypes.object,
  onError: PropTypes.func,
  fallbackSrc: PropTypes.string,
  forceUseFallback: PropTypes.bool,
  skipLazyLoading: PropTypes.bool
};

export default ResponsiveImage;
